import { useEffect, useRef } from "react";
import { LANGS } from "@/lib/api";
import { MicButton } from "@/components/MicButton";

const SIZE_OWN = 1.05;
const SIZE_LAST = 1.7;
const SIZE_OLDER = 1.3;
const LINE_HEIGHT_AR = 1.9;
const LINE_HEIGHT_ES = 1.5;

const textFor = (seg, lang) => (seg.source === lang ? seg.original : seg.translation);

const STATUS_LABEL = {
  es: { listening: "Escuchando...", idle: "Listo" },
  ar: { listening: "كنسمع...", idle: "واجد" },
};

const EMPTY_TEXT = {
  es: "Pulsa el micrófono y habla. La traducción aparecerá aquí.",
  ar: "ورك على الميكرو وهضر. الترجمة غادي تبان هنا.",
};

function segmentColor({ live, mine, isLast }) {
  if (live) return "italic text-slate-500";
  if (mine) return "text-slate-400";
  return isLast ? "text-white" : "text-slate-200";
}

function segmentSize({ mine, isLast }) {
  if (mine) return SIZE_OWN;
  return isLast ? SIZE_LAST : SIZE_OLDER;
}

const Segment = ({ seg, lang, isLast, fontScale }) => {
  const isAr = lang === "ar";
  const mine = seg.source === lang;
  const live = seg.status === "listening";
  const streaming = seg.status === "translating" && !mine;
  const testId = live ? `interim-${lang}` : `segment-${lang}-${mine ? "own" : "translated"}`;
  return (
    <p
      data-testid={testId}
      className={`segment-enter transition-colors duration-500 ${segmentColor({ live, mine, isLast })} ${live || streaming ? "caret" : ""}`}
      style={{
        fontSize: `${segmentSize({ mine, isLast }) * fontScale}rem`,
        lineHeight: isAr ? LINE_HEIGHT_AR : LINE_HEIGHT_ES,
        fontWeight: mine ? 400 : 600,
      }}
    >
      {textFor(seg, lang)}
      {seg.status === "error" && !mine && <span className="text-red-400 text-sm"> {isAr ? "خطأ فالترجمة" : "Error de traducción"}</span>}
    </p>
  );
};

const PanelHeader = ({ lang, listening, accent, testId }) => {
  const isAr = lang === "ar";
  const label = STATUS_LABEL[lang][listening ? "listening" : "idle"];
  return (
    <header className="flex items-center justify-between px-5 pt-4">
      <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${accent}`}>
        {isAr ? <span className="font-arabic text-sm tracking-normal">الدارجة · Darija</span> : "Español"}
      </span>
      <span className="flex items-center gap-2 text-xs text-slate-400" data-testid={`${testId}-status`}>
        <span className={`h-2 w-2 rounded-full ${listening ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`} />
        {label}
      </span>
    </header>
  );
};

export const TranscriptPanel = ({ lang, segments, listening, onMic, rotated, fontScale = 1, testId, micTestId, accent, micDisabled }) => {
  const isAr = lang === "ar";
  const endRef = useRef(null);
  const visible = segments.filter((s) => textFor(s, lang) || (s.status === "translating" && s.source !== lang));
  const lastId = visible.length ? visible[visible.length - 1].id : null;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [segments]);

  return (
    <section
      data-testid={testId}
      dir={LANGS[lang].dir}
      className={`relative flex h-full min-h-0 flex-col ${rotated ? "rotated-180" : ""} ${isAr ? "bg-[#111827]" : "bg-[#0f172a]"}`}
    >
      <PanelHeader lang={lang} listening={listening} accent={accent} testId={testId} />

      <div className={`scroll-thin flex-1 min-h-0 overflow-y-auto px-5 pb-4 pt-3 ${isAr ? "font-arabic" : ""}`}>
        {visible.length === 0 && (
          <p className="mt-6 text-slate-500" style={{ fontSize: `${SIZE_OWN * fontScale}rem` }} data-testid={`${testId}-empty`}>
            {EMPTY_TEXT[lang]}
          </p>
        )}
        <div className="flex flex-col gap-4">
          {visible.map((seg) => (
            <Segment key={seg.id} seg={seg} lang={lang} isLast={seg.id === lastId} fontScale={fontScale} />
          ))}
        </div>
        <div ref={endRef} />
      </div>

      <footer className="flex items-center justify-center pb-5 pt-2">
        <MicButton lang={lang} listening={listening} onClick={onMic} testId={micTestId} disabled={micDisabled} />
      </footer>
    </section>
  );
};
